import { ApiError, type ProblemDetails } from './ApiError';

/**
 * Base URL of the API.
 *
 * The frontend is deployed to Cloudflare Pages and the API to Fly, so these are
 * genuinely separate origins in every environment — there is no same-origin shortcut to
 * fall back on, and local development mirrors production rather than proxying.
 */
const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:5180').replace(
  /\/$/,
  '',
);

export interface RequestOptions extends Omit<RequestInit, 'body' | 'headers'> {
  readonly body?: unknown;

  /**
   * Narrowed to a record on purpose.
   *
   * `HeadersInit` also permits `string[][]` and `Headers`, and spreading either into an
   * object literal produces numeric indices instead of header names — a silent
   * corruption that only shows up as a missing Content-Type.
   */
  readonly headers?: Record<string, string>;

  /**
   * A body to send as-is, without JSON encoding.
   *
   * For `FormData`: the browser has to generate the multipart boundary and set
   * Content-Type itself, so setting that header manually breaks the upload in a way that
   * looks like a server bug.
   */
  readonly rawBody?: BodyInit;
}

export async function apiFetch<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { body, rawBody, headers, ...rest } = options;

  // Spread rather than assign `body: undefined`. tsconfig sets
  // exactOptionalPropertyTypes, so an explicit undefined is not assignable to
  // RequestInit's `body?: BodyInit | null`.
  const response = await fetch(`${BASE_URL}${path}`, {
    ...rest,
    headers: {
      Accept: 'application/json',
      // No Content-Type for a raw body: FormData needs the browser to set it, boundary
      // included.
      ...(body === undefined || rawBody !== undefined
        ? {}
        : { 'Content-Type': 'application/json' }),
      ...headers,
    },
    ...(rawBody !== undefined
      ? { body: rawBody }
      : body === undefined
        ? {}
        : { body: JSON.stringify(body) }),

    // Required for the refresh cookie to travel cross-origin. The API's CORS policy
    // names this origin explicitly and allows credentials, which is why the allowlist
    // can never become a wildcard.
    credentials: 'include',
  });

  if (!response.ok) {
    throw new ApiError(response.status, await readProblem(response));
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

/**
 * Parses a ProblemDetails body, tolerating one that is not there.
 *
 * A proxy or an infrastructure error can produce a non-JSON error body, and throwing
 * while handling an error would replace a useful status code with a parse failure.
 */
async function readProblem(response: Response): Promise<ProblemDetails> {
  try {
    const contentType = response.headers.get('content-type') ?? '';

    if (contentType.includes('json')) {
      return (await response.json()) as ProblemDetails;
    }
  } catch {
    // Fall through to the generic shape below.
  }

  return {
    title: response.statusText || 'The request failed.',
    status: response.status,
  };
}
